import React from 'react';
import styled from 'styled-components';

const Privacy = () => {
  return (
    <PageContainer>
      <Section>
        <ContentWrapper>
          <PagePill>Data Protection</PagePill>

          <MainHeading>Privacy Policy</MainHeading>

          <IntroText>
            This Privacy Policy explains how Sonorai (a brand operated by
            Elgazzar Ventures LLC, "we", "us", or "our") collects, uses, and protects your
            personal data when you visit sonorai.co or otherwise interact with
            us. We process personal data in compliance with the EU General Data
            Protection Regulation (Regulation 2016/679, "GDPR") and the Italian
            Personal Data Protection Code (Legislative Decree 196/2003 as
            amended).
          </IntroText>

          <ContentSection>
            <SectionHeading>1. Data Controller</SectionHeading>
            <InfoText>
              The data controller responsible for processing your personal data
              is:
            </InfoText>
            <InfoText>
              Elgazzar Ventures LLC (Sonorai)<br />
              33 North Gould Street, Sheridan, WY 82801, United States<br />
              Phone: <a href="tel:+17372822831">+1 (737) 282-2831</a><br />
              Email: <a href="mailto:mohamed@sonorai.co">mohamed@sonorai.co</a>
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>2. Data We Collect</SectionHeading>
            <InfoText>
              Depending on how you interact with us, we may process the
              following categories of personal data:
            </InfoText>
            <BulletList>
              <li>Identification and contact data: name, email address, phone number, company name, role.</li>
              <li>Communication content: the content of messages, form submissions, inquiries, and any information you choose to share with us.</li>
              <li>Booking and appointment data: details you provide when scheduling a call (e.g., via Cal.com).</li>
              <li>SMS and messaging consent records: timestamp, message of consent, and the phone number you provided.</li>
              <li>Technical and usage data: IP address, browser type and version, operating system, device identifiers, referring URL, pages visited, date and time of access.</li>
              <li>Cookies and similar technologies: see our <a href="#cookies">Cookie Policy</a>.</li>
              <li>Analytics data: aggregated or pseudonymous data about how the website is used (only where deployed and where required, with consent).</li>
            </BulletList>
          </ContentSection>

          <ContentSection>
            <SectionHeading>3. Purposes of Processing</SectionHeading>
            <InfoText>
              We process personal data for the following purposes:
            </InfoText>
            <BulletList>
              <li>Responding to your inquiries and providing information you request.</li>
              <li>Delivering the services agreed with you, including AI automation, CRM, workflow, and consulting engagements.</li>
              <li>Managing the customer relationship, including invoicing, support, and operational communications.</li>
              <li>Sending appointment, service-update, and customer-support communications (including by SMS) related to your inquiry or engagement.</li>
              <li>Sending marketing communications only where you have given separate, explicit consent.</li>
              <li>Operating, maintaining, securing, and improving the website.</li>
              <li>Producing aggregated analytics about website use.</li>
              <li>Complying with legal, tax, accounting, and regulatory obligations.</li>
              <li>Establishing, exercising, or defending legal claims.</li>
            </BulletList>
          </ContentSection>

          <ContentSection>
            <SectionHeading>4. Legal Bases (Art. 6 GDPR)</SectionHeading>
            <BulletList>
              <li><strong>Consent (Art. 6(1)(a) GDPR):</strong> for marketing communications, non-essential cookies, and SMS opt-ins.</li>
              <li><strong>Contract / pre-contractual steps (Art. 6(1)(b) GDPR):</strong> to answer your inquiry, prepare a proposal, and perform our services.</li>
              <li><strong>Legitimate interests (Art. 6(1)(f) GDPR):</strong> to keep the website secure, prevent abuse, run aggregated analytics, and run our business efficiently. You may object at any time.</li>
              <li><strong>Legal obligation (Art. 6(1)(c) GDPR):</strong> to comply with tax, accounting, and other legal requirements applicable to us.</li>
            </BulletList>
          </ContentSection>

          <ContentSection>
            <SectionHeading>5. SMS / Text-Message Communications</SectionHeading>
            <InfoText>
              If you provide your phone number and opt in to receive text
              messages from us, we will use it to send the categories of
              messages you have consented to (for example, non-marketing
              messages about your inquiry, appointments, and service updates,
              and, separately, marketing messages where consented). Message
              frequency may vary. Message and data rates may apply. You can
              reply STOP at any time to opt out, or HELP for assistance.
            </InfoText>
            <Highlight>
              SMS opt-in consent and phone numbers collected for SMS purposes
              will not be sold, rented, shared, or disclosed to third parties
              or affiliates for their own marketing purposes.
            </Highlight>
          </ContentSection>

          <ContentSection>
            <SectionHeading>6. Service Providers and Processors</SectionHeading>
            <InfoText>
              We may share data with service providers that help us operate our
              website, CRM, communications, and SMS delivery, but only as
              needed to provide our services and subject to appropriate
              safeguards. Typical categories of processors we engage include:
            </InfoText>
            <BulletList>
              <li>Website and infrastructure hosting providers.</li>
              <li>CRM and pipeline platforms (e.g., GoHighLevel).</li>
              <li>Email delivery and transactional email providers.</li>
              <li>SMS and voice messaging providers (e.g., Twilio).</li>
              <li>Scheduling and calendar tools (e.g., Cal.com).</li>
              <li>Embedded media providers (e.g., YouTube via the privacy-enhanced "nocookie" mode).</li>
              <li>Analytics tools, where deployed.</li>
              <li>Automation and workflow tooling used to operate our business.</li>
            </BulletList>
            <InfoText>
              Each processor acts on documented instructions and under a data
              processing agreement in line with Art. 28 GDPR where applicable.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>7. International Transfers</SectionHeading>
            <InfoText>
              Some of our processors are established outside the European
              Economic Area (for example, in the United States). Where data is
              transferred outside the EEA, we rely on appropriate safeguards
              under Chapter V GDPR, such as the European Commission's Standard
              Contractual Clauses and, where applicable, the EU-US Data Privacy
              Framework. You can request more information about transfers and
              safeguards by contacting us.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>8. Data Retention</SectionHeading>
            <InfoText>
              We retain personal data only for as long as necessary for the
              purposes described in this policy or as required by applicable
              law (for example, accounting and tax records are retained for the
              statutory periods under Italian law). When data is no longer
              needed, we delete or anonymize it.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>9. Your Rights</SectionHeading>
            <InfoText>
              Subject to the conditions set by the GDPR, you have the right
              to:
            </InfoText>
            <BulletList>
              <li>Access your personal data (Art. 15).</li>
              <li>Request correction of inaccurate or incomplete data (Art. 16).</li>
              <li>Request erasure of your data (Art. 17).</li>
              <li>Restrict processing (Art. 18).</li>
              <li>Object to processing, including processing based on legitimate interests and direct marketing (Art. 21).</li>
              <li>Data portability (Art. 20).</li>
              <li>Withdraw consent at any time, without affecting the lawfulness of processing carried out before withdrawal (Art. 7(3)).</li>
              <li>Lodge a complaint with a supervisory authority. In Italy this is the Garante per la protezione dei dati personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</a>).</li>
            </BulletList>
            <InfoText>
              To exercise your rights, contact us at{' '}
              <a href="mailto:mohamed@sonorai.co">mohamed@sonorai.co</a>. We
              will respond within the timelines set by the GDPR, normally
              within one month.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>10. Security</SectionHeading>
            <InfoText>
              We implement appropriate technical and organizational measures to
              protect personal data against accidental or unlawful destruction,
              loss, alteration, unauthorized disclosure, or access. No
              transmission over the Internet or method of electronic storage is
              fully secure, and we cannot guarantee absolute security.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>11. Children</SectionHeading>
            <InfoText>
              Our services are intended for business users and are not directed
              to children under 16. We do not knowingly collect personal data
              from children.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>12. Cookies</SectionHeading>
            <InfoText>
              Information about cookies and similar technologies used on this
              site is available in our <a href="#cookies">Cookie Policy</a>.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>13. Changes to This Policy</SectionHeading>
            <InfoText>
              We may update this Privacy Policy from time to time. Material
              changes will be communicated by updating this page and the "last
              updated" date below.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>14. Contact</SectionHeading>
            <InfoText>
              For any privacy-related question or request, contact us at:
            </InfoText>
            <InfoText>
              Elgazzar Ventures LLC (Sonorai)<br />
              33 North Gould Street, Sheridan, WY 82801, United States<br />
              Phone: <a href="tel:+17372822831">+1 (737) 282-2831</a><br />
              Email: <a href="mailto:mohamed@sonorai.co">mohamed@sonorai.co</a>
            </InfoText>
          </ContentSection>

          <LastUpdated>Company details updated: September 10, 2026</LastUpdated>
        </ContentWrapper>
      </Section>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: var(--bg-0);
`;

const Section = styled.section`
  padding: 120px 20px 100px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 780px;
  margin: 0 auto;
`;

const PagePill = styled.div`
  display: inline-block;
  padding: 6px 14px;
  background-color: var(--bg-2);
  border: 1px solid var(--line-2);
  border-radius: 9999px;
  color: var(--fg-2);
  font-family: var(--mono);
  font-size: 11px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.28px;
  margin-bottom: 24px;
  align-self: flex-start;
`;

const MainHeading = styled.h1`
  font-family: var(--display);
  font-size: 56px;
  font-weight: 400;
  letter-spacing: -1.6px;
  color: var(--fg-0);
  margin: 0 0 24px;
  line-height: 1.05;

  @media (max-width: 768px) {
    font-size: 40px;
    letter-spacing: -1px;
  }
`;

const IntroText = styled.p`
  font-family: var(--body);
  font-size: 17px;
  line-height: 1.55;
  color: var(--fg-2);
  margin: 0 0 48px;
  max-width: 680px;
`;

const ContentSection = styled.div`
  margin-bottom: 36px;
`;

const SectionHeading = styled.h2`
  font-family: var(--body);
  font-size: 18px;
  font-weight: 600;
  color: var(--fg-0);
  margin: 0 0 12px;
  letter-spacing: -0.2px;
`;

const InfoText = styled.p`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-1);
  margin: 0 0 12px;

  a { color: var(--blue); text-decoration: underline; }
  a:hover { color: var(--fg-0); }
`;

const BulletList = styled.ul`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-1);
  padding-left: 22px;
  margin: 4px 0 12px;

  li { margin: 6px 0; }
  a { color: var(--blue); text-decoration: underline; }
  a:hover { color: var(--fg-0); }
`;

const Highlight = styled.p`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-0);
  background: var(--bg-1);
  border-left: 3px solid var(--fg-0);
  padding: 16px 18px;
  margin: 12px 0 0;
  border-radius: 0 8px 8px 0;
`;

const LastUpdated = styled.p`
  font-family: var(--mono);
  font-size: 11px;
  color: var(--fg-3);
  text-transform: uppercase;
  letter-spacing: 0.28px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid var(--line-2);
`;

export default Privacy;
